import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BottomNav } from '@/components/sanctuary';
import { RealmNode, LightIndicator, RestAtShoreButton, InnerWorldOnboarding } from '@/components/innerworld';
import { useInnerWorldProgress } from '@/hooks/useInnerWorld';
import { logError } from '@/lib/logger';

export default function InnerWorldPage() {
  const navigate = useNavigate();
  const { progress, loading, error, completeOnboarding, selectRealm } = useInnerWorldProgress();
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setAnimateIn(true), 100);
    return () => clearTimeout(t);
  }, []);

  const handleRealmTap = async (realmId) => {
    try { await selectRealm(realmId); } catch (err) { logError('Realm select failed (non-blocking):', err?.response?.status); }
    navigate(`/inner-world/quest/${realmId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#FBF9F6' }}>
        <div className="w-3 h-3 rounded-full animate-pulse" style={{ background: '#C9A84C', boxShadow: '0 0 20px rgba(201,168,76,0.6)' }} />
      </div>
    );
  }

  if (error || !progress) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#FBF9F6' }}>
        <p className="text-sm text-center" style={{ fontFamily: 'Jost, sans-serif', color: '#6B6878' }}>{error || 'The path is quiet right now.'}</p>
      </div>
    );
  }

  const realms = progress.realms || {};

  return (
    <div className="min-h-screen pb-32" style={{ background: '#FBF9F6' }} data-testid="inner-world-map">
      {!progress.onboarding_seen && <InnerWorldOnboarding onComplete={completeOnboarding} />}

      <header className="max-w-md mx-auto px-6 pt-8 pb-2 flex items-center justify-between">
        <RestAtShoreButton />
        <LightIndicator total={progress.light_total} />
      </header>

      <main className={`max-w-md mx-auto px-6 transition-all duration-700 ${animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <div className="text-center mt-6 mb-10">
          <span className="text-xs tracking-[0.2em] block mb-3" style={{ fontFamily: 'Jost, sans-serif', color: '#C9A84C' }}>
            THE INNER WORLD
          </span>
          <p className="text-base leading-relaxed" style={{ fontFamily: 'Playfair Display, serif', color: '#4A4A5A' }}>
            No battles. No deadlines. Everything here is a part of you.
          </p>
        </div>

        <div className="relative space-y-8">
          <div className="absolute left-1/2 top-4 bottom-4 w-px" style={{ background: 'linear-gradient(to bottom, rgba(143,169,190,0.25), rgba(138,158,143,0.25), rgba(159,151,193,0.25), rgba(192,133,107,0.25), rgba(212,163,115,0.25))' }} />
          {[1, 2, 3, 4, 5].map((id, i) => {
            const info = realms[String(id)] || {};
            return (
              <div key={id} className="relative">
                <RealmNode
                  realmId={id}
                  name={info.name}
                  subtitle={info.subtitle}
                  color={info.color}
                  locked={!progress.realms_unlocked.includes(id)}
                  current={progress.current_realm === id}
                  entryCount={progress.entries_by_realm?.[String(id)] || 0}
                  weather={progress.weather_by_realm?.[String(id)]}
                  worldElements={progress.world_by_realm?.[String(id)]}
                  align={i % 2 === 0 ? 'left' : 'right'}
                  onTap={() => handleRealmTap(id)}
                />
              </div>
            );
          })}
        </div>

        <button
          onClick={() => navigate('/inner-world/council')}
          className="w-full mt-10 p-5 rounded-2xl flex items-center justify-between transition-all active:scale-[0.98]"
          style={{ background: '#F7F1EE' }}
          data-testid="council-roster-link"
        >
          <div className="text-left">
            <h3 className="text-xs tracking-[0.1em] mb-1" style={{ fontFamily: 'Jost, sans-serif', color: '#C9A84C' }}>
              YOUR COUNCIL
            </h3>
            <p className="text-sm" style={{ fontFamily: 'Jost, sans-serif', color: '#8A6A61' }}>
              {progress.council_count > 0
                ? `${progress.council_count} ${progress.council_count === 1 ? 'part' : 'parts'} of you, gathered`
                : 'Your Council is waiting to grow'}
            </p>
          </div>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#8A8798" strokeWidth="1.5">
            <path d="M9 6l6 6-6 6" />
          </svg>
        </button>

        <div className="grid grid-cols-2 gap-4 mt-4">
          <button
            onClick={() => navigate('/inner-world/home')}
            className="p-5 rounded-2xl text-left transition-all active:scale-[0.98]"
            style={{ background: '#F7F1EE' }}
            data-testid="inner-home-link"
          >
            <h3 className="text-xs tracking-[0.1em] mb-1" style={{ fontFamily: 'Jost, sans-serif', color: '#c0856b' }}>
              INNER HOME
            </h3>
            <p className="text-sm" style={{ fontFamily: 'Jost, sans-serif', color: '#8A6A61' }}>
              {progress.home_elements_count > 0
                ? `${progress.home_elements_count} ${progress.home_elements_count === 1 ? 'room' : 'rooms'} restored`
                : 'A house, filling in'}
            </p>
          </button>
          <button
            onClick={() => navigate('/inner-world/book')}
            className="p-5 rounded-2xl text-left transition-all active:scale-[0.98]"
            style={{ background: '#F7F1EE' }}
            data-testid="book-of-becoming-link"
          >
            <h3 className="text-xs tracking-[0.1em] mb-1" style={{ fontFamily: 'Jost, sans-serif', color: '#C9A84C' }}>
              BOOK OF BECOMING
            </h3>
            <p className="text-sm" style={{ fontFamily: 'Jost, sans-serif', color: '#8A6A61' }}>
              {progress.keepsake_count > 0
                ? `${progress.keepsake_count} ${progress.keepsake_count === 1 ? 'chapter' : 'chapters'} written`
                : 'Gathering itself'}
            </p>
          </button>
        </div>
      </main>

      <BottomNav currentPage="home" />
    </div>
  );
}
